// Structure of the two Mexican identity keys. Both carry a date: the CURP
// (Clave Única de Registro de Población, RENAPO) the birth date of a person;
// the RFC (Registro Federal de Contribuyentes, SAT) the birth date of an
// individual or the incorporation date of a legal entity. The portal uses
// them to validate what is captured and to fill those dates in automatically.
import { formatIsoDate, isValidDate, today } from "./dates";
import type { IsoDate } from "./types";

/** RFC: 3 (legal entity) or 4 (individual) letters, 6-digit date, 3-character homoclave. */
export const RFC_PATTERN = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/;

/** State of birth codes of the CURP; NE = born abroad. */
const CURP_STATES =
  "AS|BC|BS|CC|CL|CM|CS|CH|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE";

/**
 * CURP: 4 letters from the name, birth date (YYMMDD), sex (H, M or X), state
 * of birth, 3 consonants from the name, a homonymy character (a digit for
 * people born before 2000, a letter after) and a check digit.
 */
export const CURP_PATTERN = new RegExp(
  `^[A-Z][AEIOUX][A-Z]{2}\\d{6}[HMX](${CURP_STATES})[B-DF-HJ-NP-TV-Z]{3}[A-Z\\d]\\d$`,
);

/** Same shape, to find a CURP inside longer text. */
export const CURP_IN_TEXT = new RegExp(
  `\\b[A-Z][AEIOUX][A-Z]{2}\\d{6}[HMX](?:${CURP_STATES})[B-DF-HJ-NP-TV-Z]{3}[A-Z\\d]\\d\\b`,
  "g",
);

const CURP_ALPHABET = "0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ";

/** Check digit of the first 17 characters of a CURP (RENAPO algorithm). */
export function curpCheckDigit(first17: string): number {
  let sum = 0;
  for (let i = 0; i < 17; i++) sum += CURP_ALPHABET.indexOf(first17.charAt(i)) * (18 - i);
  return (10 - (sum % 10)) % 10;
}

const curpDate = (curp: string): IsoDate => {
  const yy = Number(curp.slice(4, 6));
  // The homonymy character tells the century: a digit before 2000, a letter from 2000 on.
  const century = /\d/.test(curp.charAt(16)) ? 1900 : 2000;
  return formatIsoDate(century + yy, Number(curp.slice(6, 8)), Number(curp.slice(8, 10)));
};

/** Shape and date are valid. Does not check the check digit (see `curpCheckDigitMatches`). */
export const isValidCurp = (curp: string) => CURP_PATTERN.test(curp) && isValidDate(curpDate(curp));

export const curpCheckDigitMatches = (curp: string) =>
  isValidCurp(curp) && curpCheckDigit(curp.slice(0, 17)) === Number(curp.charAt(17));

export interface CurpData {
  birthDate: IsoDate;
  sex: "H" | "M" | "X";
  /** Two-letter state of birth; "NE" when born abroad. */
  birthState: string;
}

/** What a valid CURP says about its holder, or null if it is not a valid CURP. */
export function decodeCurp(curp: string): CurpData | null {
  if (!isValidCurp(curp)) return null;
  return {
    birthDate: curpDate(curp),
    sex: curp.charAt(10) as CurpData["sex"],
    birthState: curp.slice(11, 13),
  };
}

/**
 * Date encoded in an RFC: birth date (individuals, 13 characters) or
 * incorporation date (legal entities, 12 characters). The RFC has a
 * two-digit year, so the most recent century that does not put the date
 * after `notAfter` is taken: today by default, or a later known event such as
 * the date the entity started operations. Null if the RFC or its date is invalid.
 */
export function dateFromRfc(rfc: string, notAfter: IsoDate = today()): IsoDate | null {
  if (!RFC_PATTERN.test(rfc)) return null;
  const digits = rfc.slice(rfc.length - 9, rfc.length - 3);
  const yy = Number(digits.slice(0, 2));
  const mm = Number(digits.slice(2, 4));
  const dd = Number(digits.slice(4, 6));
  const recent = formatIsoDate(2000 + yy, mm, dd);
  const date = recent <= notAfter ? recent : formatIsoDate(1900 + yy, mm, dd);
  return isValidDate(date) ? date : null;
}

/** An RFC of 13 characters belongs to an individual; one of 12, to a legal entity. */
export const rfcPersonType = (rfc: string): "individual" | "legal_entity" | null =>
  RFC_PATTERN.test(rfc) ? (rfc.length === 13 ? "individual" : "legal_entity") : null;
